import useSEO from '@/hooks/useSEO';
import { slippageFAQs } from './data';
import SlippageTypesSection from './components/SlippageTypesSection';
import SlippageTriggersSection from './components/SlippageTriggersSection';
import SlippageCostSection from './components/SlippageCostSection';
import SlippageOrderTypeSection from './components/SlippageOrderTypeSection';
import BrokerExecutionSection from './components/BrokerExecutionSection';
import ReduceSlippageSection from './components/ReduceSlippageSection';
import SlippageFAQSection from './components/SlippageFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function ForexSlippage() {
  useSEO({
    /* @replace:slippage-title */

    title: 'Forex Slippage Explained: Causes, Cost and Prevention',
    /* @replace:slippage-description */

    description: 'Learn what causes positive and negative slippage, how order type and market conditions affect fills, and practical ways to limit execution cost.',
    /* @replace:slippage-keywords */

    keywords: 'forex slippage, positive slippage, negative slippage, market order slippage, reduce slippage, execution cost',
    /* @replace:slippage-canonical */

    canonical: '/forex-slippage/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: slippageFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950">
        {/* Decorative: fine dot grid — order book / depth chart texture */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--secondary-500)) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Decorative: offset concentric rings — visual metaphor for price divergence */}
        <div className="absolute -top-[200px] -right-[140px] w-[600px] h-[600px] rounded-full border border-secondary-500/10" />
        <div className="absolute -top-[160px] -right-[100px] w-[520px] h-[520px] rounded-full border border-secondary-500/8" />
        <div className="absolute -top-[120px] -right-[60px] w-[440px] h-[440px] rounded-full border border-secondary-500/6" />

        {/* Second ring cluster — offset to suggest misalignment */}
        <div className="absolute -bottom-[180px] -left-[100px] w-[540px] h-[540px] rounded-full border border-accent-500/8" />
        <div className="absolute -bottom-[130px] -left-[60px] w-[440px] h-[440px] rounded-full border border-accent-500/6" />
        <div className="absolute -bottom-[80px] -left-[20px] w-[340px] h-[340px] rounded-full border border-accent-500/4" />

        {/* Decorative: diverging diagonal lines — price gap motif */}
        <div className="absolute top-[80px] left-[8%] w-[160px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/30 to-accent-500/0 rotate-[12deg]" />
        <div className="absolute top-[84px] left-[8%] w-[130px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/18 to-accent-500/0 -rotate-[8deg]" />
        <div className="absolute top-[180px] right-[12%] w-[140px] h-[1px] bg-gradient-to-r from-secondary-500/0 via-secondary-500/25 to-secondary-500/0 -rotate-[15deg]" />
        <div className="absolute top-[184px] right-[12%] w-[110px] h-[1px] bg-gradient-to-r from-secondary-500/0 via-secondary-500/14 to-secondary-500/0 rotate-[10deg]" />

        {/* Decorative: thin vertical bars — execution speed / millisecond markers */}
        <div className="absolute top-[20%] right-[5%] flex gap-3">
          <div className="w-[1px] h-14 bg-secondary-500/10" />
          <div className="w-[1px] h-24 bg-accent-500/14" />
          <div className="w-[1px] h-8 bg-secondary-500/6" />
          <div className="w-[1px] h-18 bg-secondary-500/10" />
        </div>
        <div className="absolute bottom-[25%] left-[4%] flex gap-3">
          <div className="w-[1px] h-20 bg-accent-500/8" />
          <div className="w-[1px] h-12 bg-accent-500/12" />
          <div className="w-[1px] h-28 bg-accent-500/6" />
        </div>

        {/* Decorative: scattered tiny crosses */}
        <span className="absolute top-[35%] right-[20%] text-accent-500/12 text-[16px] font-light select-none">+</span>
        <span className="absolute bottom-[30%] left-[14%] text-secondary-500/10 text-[14px] font-light select-none">+</span>
        <span className="absolute top-[55%] left-[22%] text-accent-500/8 text-[12px] font-light select-none">+</span>

        {/* Decorative: large soft glow behind center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[750px] h-[500px] rounded-full opacity-[0.05] blur-[140px] bg-accent-500" />

        {/* Main content */}
        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-16 md:pt-24 pb-16 md:pb-20 text-center">
          {/* Heading */}
          <h1 className="text-[38px] md:text-[56px] font-[750] text-background-50 leading-[1.08] tracking-tight mb-5">
            What is
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">forex slippage</span>
              <span className="absolute left-0 right-0 bottom-[2px] h-[5px] rounded-full bg-accent-500/60" />
            </span>
          </h1>

          <p className="text-[16px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[620px] mx-auto mb-10">
            The gap between the price you click and the price you get. Sometimes costs you money, sometimes saves it. Mostly costs.
          </p>

          {/* ── Core visual: The Price Gap ── */}
          <div className="max-w-[860px] mx-auto mb-8">
            {/* Formula centerpiece with visual gap representation */}
            <div className="relative bg-foreground-900/80 border border-foreground-800/60 rounded-[24px] p-6 md:p-10 overflow-hidden">
              {/* Subtle inner glow */}
              <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-accent-500/3 rounded-full blur-[100px] pointer-events-none" />

              <div className="relative">
                {/* Formula label */}
                <div className="text-[12px] font-bold uppercase tracking-[0.15em] text-foreground-500 mb-6">
                  The definition
                </div>

                {/* Visual price gap representation */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-8">
                  {/* Requested price box */}
                  <div className="w-full md:w-auto flex-1 max-w-[240px] bg-foreground-800/60 rounded-xl p-5 border border-foreground-700/40">
                    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground-500 mb-2">You click</div>
                    <div className="text-[28px] md:text-[34px] font-[800] text-background-50 leading-none tracking-tight tabular-nums">
                      1.0850
                    </div>
                    <div className="text-[11px] text-foreground-500 mt-1">Requested price</div>
                  </div>

                  {/* The gap arrow */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className="hidden md:flex items-center gap-1">
                      <div className="w-10 h-[2px] bg-accent-500/40 rounded-full" />
                      <div className="w-3 h-3 border-t-2 border-r-2 border-accent-500/40 rotate-45 -ml-1" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-accent-400 whitespace-nowrap">
                      Execution gap
                    </span>
                    <div className="md:hidden flex items-center gap-1">
                      <div className="w-16 h-[2px] bg-accent-500/40 rounded-full" />
                      <div className="w-3 h-3 border-t-2 border-r-2 border-accent-500/40 rotate-45 -ml-1" />
                    </div>
                  </div>

                  {/* Executed price box */}
                  <div className="w-full md:w-auto flex-1 max-w-[240px] bg-foreground-800/60 rounded-xl p-5 border border-foreground-700/40">
                    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground-500 mb-2">You get</div>
                    <div className="text-[28px] md:text-[34px] font-[800] text-background-50 leading-none tracking-tight tabular-nums">
                      1.0852
                    </div>
                    <div className="text-[11px] text-foreground-500 mt-1">Executed price</div>
                  </div>
                </div>

                {/* Formula line */}
                <div className="flex items-center justify-center gap-3 md:gap-5 flex-wrap">
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">Formula</span>
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center">
                    <span className="text-[20px] md:text-[28px] font-[750] text-background-50 tracking-tight">Slippage</span>
                    <span className="text-[16px] md:text-[24px] font-[300] text-foreground-500">=</span>
                    <span className="text-[20px] md:text-[28px] font-[750] text-accent-400 tracking-tight">Executed</span>
                    <span className="text-[16px] md:text-[24px] font-[300] text-foreground-500">−</span>
                    <span className="text-[20px] md:text-[28px] font-[750] text-background-50 tracking-tight">Requested</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom insight line */}
          <p className="mt-8 text-[13px] text-foreground-500 max-w-[620px] mx-auto leading-relaxed">
            Slippage is not a broker scam — it is a <strong className="text-foreground-400">market mechanic</strong>. How much you experience depends on your order type, trade timing, pair liquidity, and broker execution model.
          </p>
        </div>
      </section>

      <SlippageTypesSection />
      <SlippageTriggersSection />
      <SlippageCostSection />
      <SlippageOrderTypeSection />
      <BrokerExecutionSection />
      <ReduceSlippageSection />
      <SlippageFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}